import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import { Modal, LoginModal } from "../../components";
import { FeedbackForm } from "../../pages/Contacts/Forms/FeedbackForm";
import { contactsSelector } from "../../redux/contacts/selectors";
import { isAuthenticatedSelector } from "../../redux/profile/authAndReg/selectors";
import { setShowWindow } from "../../redux/profile/authAndReg/slice";
import { useAppDispatch } from "../../redux/store";
import { fetchContacts } from "../../redux/contacts/asyncActions";
import { formatPhone } from "../../utils";

import styles from "./footer.module.scss";
import logo from "../../assets/img/logo.svg";
import { WindowNames } from "../../resources/constants";
import { ModalTexts } from "../../resources/locale";

export const Footer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { contacts } = useSelector(contactsSelector);
  const auth = useSelector(isAuthenticatedSelector);
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = React.useState(false);

  React.useEffect(() => {
    dispatch(fetchContacts());
  }, [dispatch]);

  React.useEffect(() => {
    if (auth && isLoginModalOpen) {
      setIsLoginModalOpen(false);
      setIsFeedbackOpen(true);
    }
  }, [auth, isLoginModalOpen]);

  const handleContactUs = () => {
    if (!auth) {
      dispatch(setShowWindow(WindowNames.AUTH));
      setIsLoginModalOpen(true);
    } else {
      setIsFeedbackOpen(true);
    }
  };

  const phone = contacts?.phone_number || "+7 (495) 150-55-99";
  const address = contacts?.address || "г. Москва, ул. Летовская Малая, 8";
  const instagramLink = contacts?.network_link_1 || "";
  const facebookLink = contacts?.network_link_2 || "";

  return (
    <>
      <footer className={styles.footer}>
        <div className="container">
          <div className={styles.wrapper}>
            <div className={styles.logoSection}>
              <Link to="/" className={styles.logo}>
                <img src={logo} alt="Company's logo" />
              </Link>
              <Link to="/politics" className={styles.privacy}>
                Политика конфиденциальности
              </Link>
            </div>

            <nav className={styles.menu}>
              <div className={styles.menuSection}>
                <h3 className={styles.menuTitle}>Услуги</h3>
                <ul className={styles.menuList}>
                  <li>
                    <Link to="/subscriptions">Абонементы</Link>
                  </li>
                  <li>
                    <Link to="/halls">Аренда залов</Link>
                  </li>
                </ul>
              </div>

              <div className={styles.menuSection}>
                <h3 className={styles.menuTitle}>О клубе</h3>
                <ul className={styles.menuList}>
                  <li>
                    <Link to="/about">Клуб</Link>
                  </li>
                  <li>
                    <Link to="/halls">Спортивные залы</Link>
                  </li>
                </ul>
              </div>

              <div className={styles.menuSection}>
                <h3 className={styles.menuTitle}>Обратная связь</h3>
                <ul className={styles.menuList}>
                  <li>
                    <Link to="/contacts">Контакты</Link>
                  </li>
                  <li>
                    <button 
                      type="button" 
                      className={styles.contactUsButton}
                      onClick={handleContactUs}
                    >
                      Связаться с нами
                    </button>
                  </li>
                </ul>
              </div>
            </nav>

            <div className={styles.contactsSection}>
              <div className={styles.social}>
                {instagramLink && (
                  <a 
                    href={instagramLink} 
                    target="_blank" 
                    rel="noreferrer"
                    className={styles.socialIcon}
                    aria-label="Instagram"
                  >
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M25.1665 1.33326H6.72925C6.01159 1.33238 5.30097 1.47481 4.63907 1.7522C3.97718 2.02959 3.37734 2.43636 2.87474 2.94864C2.37213 3.46092 1.97688 4.06841 1.71216 4.73547C1.44743 5.40253 1.31858 6.11574 1.33314 6.83326V10.4999C1.33314 10.662 1.39753 10.8174 1.51213 10.932C1.62674 11.0466 1.78218 11.111 1.94425 11.111H8.05536C8.21744 11.111 8.37288 11.0466 8.48748 10.932C8.60209 10.8174 8.66647 10.662 8.66647 10.4999C8.66647 10.3378 8.60209 10.1824 8.48748 10.0678C8.37288 9.9532 8.21744 9.88881 8.05536 9.88881H2.55536V6.83326C2.54259 6.27674 2.64106 5.72327 2.84499 5.2053C3.04892 4.68734 3.35422 4.2153 3.74297 3.81687C4.13173 3.41844 4.59612 3.10164 5.10891 2.88503C5.62171 2.66843 6.17259 2.55639 6.72925 2.55548H25.1665C26.3006 2.55677 27.3879 3.00788 28.1899 3.80984C28.9919 4.6118 29.443 5.69912 29.4443 6.83326V9.88881H23.9443C23.7822 9.88881 23.6267 9.9532 23.5121 10.0678C23.3975 10.1824 23.3331 10.3378 23.3331 10.4999C23.3331 10.662 23.3975 10.8174 23.5121 10.932C23.6267 11.0466 23.7822 11.111 23.9443 11.111H30.0554C30.2174 11.111 30.3729 11.0466 30.4875 10.932C30.6021 10.8174 30.6665 10.662 30.6665 10.4999V6.83326C30.6649 5.37506 30.0849 3.97705 29.0538 2.94596C28.0227 1.91486 26.6247 1.33487 25.1665 1.33326ZM30.0554 12.3333C29.8933 12.3333 29.7378 12.3976 29.6232 12.5122C29.5086 12.6269 29.4443 12.7823 29.4443 12.9444V25.1666C29.443 26.3007 28.9919 27.388 28.1899 28.19C27.3879 28.992 26.3006 29.4431 25.1665 29.4444H6.83314C5.699 29.4431 4.61168 28.992 3.80973 28.19C3.00777 27.388 2.55666 26.3007 2.55536 25.1666V12.9444C2.55536 12.7823 2.49098 12.6269 2.37637 12.5122C2.26177 12.3976 2.10633 12.3333 1.94425 12.3333C1.78218 12.3333 1.62674 12.3976 1.51213 12.5122C1.39753 12.6269 1.33314 12.7823 1.33314 12.9444V25.1666C1.33476 26.6248 1.91474 28.0228 2.94584 29.0539C3.97694 30.085 5.37495 30.665 6.83314 30.6666H25.1665C26.6247 30.665 28.0227 30.085 29.0538 29.0539C30.0849 28.0228 30.6649 26.6248 30.6665 25.1666V12.9444C30.6665 12.7823 30.6021 12.6269 30.4875 12.5122C30.3729 12.3976 30.2174 12.3333 30.0554 12.3333Z" fill="#00CC66"/>
                    <path d="M8.6665 17.2222C8.6665 18.6726 9.0966 20.0905 9.90239 21.2964C10.7082 22.5024 11.8535 23.4423 13.1935 23.9974C14.5335 24.5524 16.008 24.6976 17.4305 24.4147C18.853 24.1317 20.1597 23.4333 21.1853 22.4077C22.2109 21.3821 22.9093 20.0754 23.1923 18.6529C23.4752 17.2304 23.33 15.7559 22.775 14.4159C22.2199 13.0759 21.28 11.9306 20.074 11.1248C18.8681 10.319 17.4502 9.88892 15.9998 9.88892C14.0556 9.89118 12.1917 10.6645 10.8169 12.0393C9.44211 13.4141 8.66877 15.278 8.6665 17.2222ZM22.1109 17.2222C22.1109 18.4309 21.7525 19.6124 21.081 20.6174C20.4095 21.6224 19.4551 22.4056 18.3385 22.8682C17.2218 23.3307 15.9931 23.4517 14.8076 23.2159C13.6222 22.9801 12.5333 22.3981 11.6786 21.5435C10.824 20.6888 10.2419 19.5999 10.0061 18.4145C9.77035 17.229 9.89137 16.0003 10.3539 14.8836C10.8164 13.767 11.5997 12.8125 12.6047 12.141C13.6097 11.4695 14.7912 11.1111 15.9998 11.1111C17.6201 11.1129 19.1734 11.7573 20.3191 12.903C21.4648 14.0487 22.1092 15.602 22.1109 17.2222Z" fill="#00CC66"/>
                    <path d="M11.1111 17.2223C11.1111 18.1892 11.3978 19.1344 11.935 19.9384C12.4722 20.7423 13.2357 21.369 14.1291 21.739C15.0224 22.109 16.0054 22.2058 16.9537 22.0172C17.9021 21.8286 18.7732 21.3629 19.4569 20.6792C20.1407 19.9955 20.6063 19.1244 20.7949 18.176C20.9836 17.2277 20.8867 16.2447 20.5167 15.3514C20.1467 14.458 19.5201 13.6945 18.7161 13.1573C17.9121 12.6201 16.9669 12.3334 16 12.3334C14.7038 12.3348 13.4611 12.8504 12.5446 13.7669C11.6281 14.6834 11.1125 15.9261 11.1111 17.2223ZM19.6666 17.2223C19.6666 17.9474 19.4516 18.6564 19.0487 19.2593C18.6458 19.8623 18.0731 20.3323 17.4031 20.6098C16.7331 20.8873 15.9959 20.9599 15.2846 20.8185C14.5734 20.677 13.92 20.3278 13.4072 19.815C12.8945 19.3022 12.5452 18.6488 12.4038 17.9376C12.2623 17.2263 12.3349 16.4891 12.6124 15.8191C12.8899 15.1491 13.3599 14.5764 13.9629 14.1735C14.5659 13.7706 15.2748 13.5556 16 13.5556C16.9721 13.5567 17.9041 13.9434 18.5914 14.6308C19.2788 15.3182 19.6655 16.2501 19.6666 17.2223ZM22.7222 8.05558C23.0848 8.05558 23.4392 7.94806 23.7407 7.74661C24.0422 7.54516 24.2772 7.25883 24.416 6.92384C24.5547 6.58884 24.591 6.22022 24.5203 5.86458C24.4496 5.50895 24.275 5.18228 24.0186 4.92589C23.7622 4.66949 23.4355 4.49488 23.0799 4.42414C22.7242 4.3534 22.3556 4.38971 22.0206 4.52847C21.6856 4.66723 21.3993 4.90221 21.1978 5.2037C20.9964 5.50519 20.8889 5.85965 20.8889 6.22225C20.8889 6.70848 21.082 7.1748 21.4258 7.51861C21.7697 7.86243 22.236 8.05558 22.7222 8.05558ZM22.7222 5.61114C22.8431 5.61114 22.9612 5.64698 23.0617 5.71413C23.1622 5.78128 23.2405 5.87672 23.2868 5.98839C23.333 6.10005 23.3451 6.22293 23.3216 6.34147C23.298 6.46002 23.2398 6.56891 23.1543 6.65437C23.0689 6.73984 22.96 6.79804 22.8414 6.82162C22.7229 6.8452 22.6 6.8331 22.4883 6.78684C22.3767 6.74059 22.2812 6.66226 22.2141 6.56177C22.1469 6.46127 22.1111 6.34312 22.1111 6.22225C22.1111 6.06017 22.1755 5.90474 22.2901 5.79013C22.4047 5.67552 22.5601 5.61114 22.7222 5.61114Z" fill="#00CC66"/>
                    </svg>
                  </a>
                )}
                {facebookLink && (
                  <a 
                    href={facebookLink} 
                    target="_blank" 
                    rel="noreferrer"
                    className={styles.socialIcon}
                    aria-label="Facebook"
                  >
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_3507_35606)">
                    <path d="M15.0221 30.6666H4.26659C2.64347 30.6666 1.33325 29.3564 1.33325 27.7333V4.26659C1.33325 2.64347 2.64347 1.33325 4.26659 1.33325H27.7333C29.3564 1.33325 30.6666 2.64347 30.6666 4.26659V27.7333C30.6666 29.3564 29.3564 30.6666 27.7333 30.6666H20.8888V19.911H23.8221L25.7777 15.0221H20.8888V11.111C20.8888 10.0355 21.7688 10.1333 22.8444 10.1333H24.7999V5.24436H20.8888C19.2657 5.24436 17.799 5.90925 16.743 6.96525C15.687 8.02125 15.0221 9.48792 15.0221 11.111V15.0221H11.111V19.911H15.0221V30.6666Z" stroke="#00CC66" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                    <clipPath id="clip0_3507_35606">
                    <rect width="32" height="32" fill="white"/>
                    </clipPath>
                    </defs>
                    </svg>
                  </a>
                )}
              </div>
              <p className={styles.phone}>
                <a href={`tel:${phone.replace(/\D/g, '')}`}>{formatPhone(phone)}</a>
              </p>
              <p className={styles.address}>
                <a 
                  href="https://yandex.ru/maps/org/tennis_ru/1341065211/?ll=37.410421%2C55.570022&z=18.54" 
                  target="_blank" 
                  rel="noreferrer"
                >
                  {address}
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {isFeedbackOpen && (
        <Modal
          title="Задать вопрос"
          subTitle=""
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
        >
          <FeedbackForm onSubmitFunc={() => setIsFeedbackOpen(false)} />
        </Modal>
      )}

      {/* Модалка авторизации */}
      {isLoginModalOpen && (
        <LoginModal isOpen={isLoginModalOpen} setIsOpen={setIsLoginModalOpen} />
      )}
    </>
  );
};
